/**
 * COMMERCE-WORKSPACE-000002 — Pricing founder workspace.
 * Business SoT for price. MarketSync domain=pricing.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  amazonConnectApi,
  type PricingPage as PricingPageResult,
  type PricingRow,
  type SellerConnection,
} from '../../api/amazonconnect';
import {
  PAGE_SIZE_OPTIONS,
  PlatformDataGrid,
  type DataGridColumn,
  type DataGridMeta,
  type PageSizeOption,
} from '../../components/platform/PlatformDataGrid';
import { getMembershipId, getWorkspaceId } from '../../lib/auth-storage';
import { FounderCommerceShell } from './workspace/FounderCommerceShell';
import {
  AsinLink,
  ComingSoonCell,
  MoneyCell,
  SkuLink,
  WorkspaceLegend,
  type FieldSource,
} from './workspace/commerceFields';
import { useCommerceDomainHealth } from './workspace/useCommerceDomainHealth';

function src(r: PricingRow, key: string, fallback: FieldSource = 'COMING_SOON'): FieldSource {
  const v = r.field_sources?.[key];
  if (
    v === 'SP-API' ||
    v === 'MarketSync' ||
    v === 'Derived' ||
    v === 'Calculated' ||
    v === 'Estimated' ||
    v === 'COMING_SOON'
  ) {
    return v;
  }
  return fallback;
}

export function MarketSyncPricingPage() {
  const workspaceKey = getWorkspaceId() ?? 'default';
  const userKey = getMembershipId() ?? 'anon';
  const [params] = useSearchParams();

  const [connections, setConnections] = useState<SellerConnection[]>([]);
  const [connectionId, setConnectionId] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PageSizeOption>(25);
  const [q, setQ] = useState('');
  const [qDraft, setQDraft] = useState('');
  const [sortCol, setSortCol] = useState('sku');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [condFilter, setCondFilter] = useState('');
  const [result, setResult] = useState<PricingPageResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [latencyMs, setLatencyMs] = useState<number | undefined>();
  const [bootstrapped, setBootstrapped] = useState(false);

  useEffect(() => {
    void amazonConnectApi
      .listConnections()
      .then((c) => {
        setConnections(c);
        const urlConn = params.get('connection') || '';
        const pick = urlConn && c.some((x) => x.id === urlConn) ? urlConn : c[0]?.id || '';
        setConnectionId((prev) => prev || pick);
      })
      .catch(() => setConnections([]));
  }, [params]);

  useEffect(() => {
    if (bootstrapped) return;
    const uq = params.get('q') || params.get('asin') || '';
    if (uq) {
      setQ(uq);
      setQDraft(uq);
    }
    setBootstrapped(true);
  }, [params, bootstrapped]);

  const connection = connections.find((c) => c.id === connectionId);
  const defaultMp =
    connection?.marketplace_ids?.[0] ?? connection?.marketplaces?.[0]?.marketplace_id;
  const health = useCommerceDomainHealth(connectionId, 'pricing');
  const sortParam = order === 'desc' ? `${sortCol}_desc` : sortCol;

  const load = useCallback(async () => {
    if (!connectionId) {
      setLoading(false);
      setResult(null);
      return;
    }
    setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      const res = await amazonConnectApi.listPricingPage(connectionId, {
        page,
        page_size: pageSize,
        q: q || undefined,
        sort: sortParam,
        order,
        status: condFilter || undefined,
      });
      setLatencyMs(Math.round(performance.now() - t0));
      setResult(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load pricing');
      setResult(null);
      setLatencyMs(undefined);
    } finally {
      setLoading(false);
    }
  }, [connectionId, page, pageSize, q, sortParam, order, condFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = result?.items ?? [];
  const meta: DataGridMeta | null = result
    ? {
        total_count: result.total_count,
        returned_count: result.returned_count ?? result.count,
        page: result.page,
        page_size: result.page_size,
        pages: result.pages,
        remaining: result.remaining,
        has_more: result.has_more,
        execution_time_ms: result.execution_time_ms ?? latencyMs,
        source: result.source,
        data_source_badge: result.source || 'MarketSync · pricing',
        last_sync: health.lastSync || connection?.last_sync_at,
        amazon_count: health.amazonCount,
      }
    : null;

  const columns: DataGridColumn<PricingRow>[] = useMemo(
    () => [
      {
        id: 'sku',
        header: 'SKU',
        width: 120,
        sortable: true,
        stickyLeft: 0,
        render: (r) =>
          src(r, 'sku') === 'COMING_SOON' ? (
            <ComingSoonCell />
          ) : (
            <SkuLink sku={r.sku} connectionId={connectionId} />
          ),
        exportValue: (r) => r.sku ?? '',
      },
      {
        id: 'asin',
        header: 'ASIN',
        width: 120,
        sortable: true,
        render: (r) =>
          src(r, 'asin') === 'COMING_SOON' || !r.asin ? (
            <ComingSoonCell />
          ) : (
            <AsinLink
              asin={r.asin}
              marketplaceId={r.marketplace_id || defaultMp}
              connectionId={connectionId}
            />
          ),
        exportValue: (r) => r.asin ?? '',
      },
      {
        id: 'price',
        header: 'Price',
        width: 130,
        sortable: true,
        render: (r) =>
          src(r, 'price') === 'COMING_SOON' || !r.listing_price ? (
            <ComingSoonCell />
          ) : (
            <MoneyCell
              listing={r.listing_price}
              shipping={r.shipping_price}
              currency={r.currency}
              highlightShipping
            />
          ),
        exportValue: (r) => r.listing_price ?? 'NOT AVAILABLE',
      },
      {
        id: 'displayed_price',
        header: 'Displayed',
        width: 96,
        render: (r) =>
          r.displayed_price && src(r, 'displayed_price') !== 'COMING_SOON' ? (
            <span
              className="font-mono tabular-nums"
              title="LOGIC-BIBLE: listing_price + COALESCE(shipping, 0)"
            >
              {r.displayed_price}
              {r.currency ? <span className="ml-1 text-[10px] text-slate-500">{r.currency}</span> : null}
            </span>
          ) : (
            <ComingSoonCell />
          ),
        exportValue: (r) => r.displayed_price ?? 'NOT AVAILABLE',
      },
      {
        id: 'bb_price',
        header: 'BB Price',
        width: 96,
        render: (r) =>
          r.bb_price && src(r, 'bb_price') !== 'COMING_SOON' ? (
            <span className="font-mono tabular-nums">{r.bb_price}</span>
          ) : (
            <ComingSoonCell />
          ),
        exportValue: (r) => r.bb_price ?? 'COMING SOON',
      },
      {
        id: 'buy_box_pct',
        header: 'Buy Box %',
        width: 96,
        render: (r) =>
          r.buy_box_pct && src(r, 'buy_box_pct') !== 'COMING_SOON' ? (
            <span className="font-mono tabular-nums">{r.buy_box_pct}</span>
          ) : (
            <ComingSoonCell />
          ),
        exportValue: (r) => r.buy_box_pct ?? 'COMING SOON',
      },
      {
        id: 'offers',
        header: 'Offers',
        width: 80,
        render: (r) =>
          r.offers != null && src(r, 'offers') !== 'COMING_SOON' ? (
            <span className="font-mono tabular-nums">{r.offers}</span>
          ) : (
            <ComingSoonCell />
          ),
        exportValue: (r) => (r.offers != null ? String(r.offers) : 'COMING SOON'),
      },
      {
        id: 'buy_box_winner_name',
        header: 'Winner',
        width: 140,
        render: (r) =>
          r.buy_box_winner_name && src(r, 'buy_box_winner_name') !== 'COMING_SOON' ? (
            <span title={`Source: ${src(r, 'buy_box_winner_name')}`}>{r.buy_box_winner_name}</span>
          ) : (
            <ComingSoonCell />
          ),
        exportValue: (r) => r.buy_box_winner_name ?? 'COMING SOON',
      },
      {
        id: 'bsr',
        header: 'BSR',
        width: 80,
        sortable: true,
        render: (r) =>
          r.bsr != null && src(r, 'bsr') !== 'COMING_SOON' ? (
            <span className="font-mono tabular-nums">{r.bsr}</span>
          ) : (
            <ComingSoonCell />
          ),
        exportValue: (r) => (r.bsr != null ? String(r.bsr) : 'COMING SOON'),
      },
    ],
    [defaultMp, connectionId],
  );

  return (
    <FounderCommerceShell
      domain="pricing"
      title="Pricing"
      subtitle="Offer price SoT for the platform. Editable only via Amazon pricing APIs — this grid is read + sync."
      entryKey="SKU"
      connection={connection}
      domainBadge={health.quality || 'pricing'}
      legend={<WorkspaceLegend />}
    >
      <PlatformDataGrid
        gridId="pricing-workspace-v2"
        workspaceKey={workspaceKey}
        userKey={userKey}
        columns={columns}
        rows={rows}
        rowKey={(r) => `${r.asin ?? ''}|${r.sku ?? ''}|${r.status ?? ''}`}
        meta={meta}
        loading={loading}
        error={error}
        emptyMessage="No pricing rows. Run Pricing sync from Connect, then refresh."
        emptyAction={
          <Link to="/amazon/connect" className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">
            Open Connect
          </Link>
        }
        page={page}
        pageSize={pageSize}
        sort={sortCol}
        order={order}
        searchDraft={qDraft}
        searchApplied={q || undefined}
        onSearchDraftChange={setQDraft}
        onSearch={() => {
          setQ(qDraft.trim());
          setPage(1);
        }}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          if (PAGE_SIZE_OPTIONS.includes(n)) {
            setPageSize(n);
            setPage(1);
          }
        }}
        onSortChange={(col, ord) => {
          setSortCol(col);
          setOrder(ord);
          setPage(1);
        }}
        onRefresh={() => void load()}
        selectable
        selectedKeys={selected}
        onSelectionChange={setSelected}
        density="compact"
        toolbarExtra={
          <>
            <label className="text-sm">
              <span className="text-xs font-medium text-slate-500">Connection</span>
              <select
                className="mt-1 block min-h-11 min-w-[16rem] rounded-lg border border-slate-300 px-2 dark:border-slate-600 dark:bg-slate-900"
                value={connectionId}
                onChange={(e) => {
                  setConnectionId(e.target.value);
                  setPage(1);
                  setSelected(new Set());
                }}
              >
                {connections.length === 0 && <option value="">No connections</option>}
                {connections.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.seller_ref || c.id.slice(0, 8)} · {c.status}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="text-xs font-medium text-slate-500">Condition</span>
              <select
                className="mt-1 block min-h-11 min-w-[8rem] rounded-lg border border-slate-300 px-2 text-sm dark:border-slate-600 dark:bg-slate-900"
                value={condFilter}
                onChange={(e) => {
                  setCondFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All</option>
                <option value="New">New</option>
                <option value="Used">Used</option>
              </select>
            </label>
          </>
        }
      />
    </FounderCommerceShell>
  );
}
